/**
 * DataLakeMap Palette Variants
 *
 * Dark and light theme palettes for map surfaces. Each palette provides
 * base-map tones (landmass, water, roads, labels, etc.) plus shared
 * fire-overlay tokens (severity ramp, beacon colors).
 *
 * ┌───────────────────────────────────────────────────────────────────┐
 * │ Palette       │ Landmass  │ Vibe                                │
 * ├───────────────┼───────────┼─────────────────────────────────────┤
 * │ deepCanvas    │ #151A28   │ Darkest, max fire glow              │
 * │ dusk          │ #1A2035   │ +1 step lighter, softer navy        │
 * │ twilight      │ #1F2742   │ +2 steps, more blue undertone       │
 * │ slate         │ #263043   │ +3 steps, warm-grey slate           │
 * │ brand         │ #0f172a   │ Matches fb-surface (design tokens)  │
 * │ daylight      │ #f8fafc   │ Light: crisp white                  │
 * │ paper         │ #f1f5f9   │ Light: warm off-white               │
 * │ cloud         │ #e2e8f0   │ Light: soft grey                    │
 * └───────────────────────────────────────────────────────────────────┘
 */

// ── Palette shape ────────────────────────────────────────────────

export type ThemeMode = 'dark' | 'light';

export interface MapPalette {
  /** Theme mode this palette belongs to */
  mode: ThemeMode;
  // Base map
  landmass: string;
  water: string;
  landuse: string;
  minorRoad: string;
  minorRoadOp: number;
  majorRoad: string;
  majorRoadOp: number;
  // Typography
  cityLabel: string;
  minorLabel: string;
  textHalo: string;
  textHaloWidth: number;
  textHaloBlur: number;
  // Topography
  contour: string;
  contourIndexOp: number;
  contourMinorOp: number;
  // Hillshade
  hillshadeShadow: string;
  hillshadeHighlight: string;
  // Buildings
  building: string;
  // Fire overlays (shared across all palettes)
  activeBorder: string;
  activeFill: string;
  activeFillOp: number;
  historicalBorder: string;
  beaconStroke: string;
  beaconStrokeW: number;
  beaconPrimary: string;
  beaconCritical: string;
  severityLow: string;
  severityMod: string;
  severityModerate: string;
  severityHigh: string;
  severityExtreme: string;
  severityFallback: string;
  vintageVeryHigh: string;
  vintageHigh: string;
  vintageModerate: string;
  vintageFallback: string;
  // Green tokens for vegetation / containment / safe states.
  vegetationGreen: string;
  containmentGreen: string;
  safeGreen: string;
}

export type PaletteName = 'deepCanvas' | 'dusk' | 'twilight' | 'slate' | 'brand'
  | 'daylight' | 'paper' | 'cloud';

// ── Shared fire overlay tokens (constant across all palettes) ────

export const FIRE_TOKENS = {
  activeBorder:     '#fbbf24',
  activeFill:       '#fbbf24',
  activeFillOp:     0.18,
  historicalBorder:  '#94a3b8',
  beaconStroke:     '#151a28',
  beaconStrokeW:    0,
  beaconPrimary:    '#fbbf24',
  beaconCritical:   '#ef4444',
  // Severity scale: true-color saturated stops so demo-grade fire data
  // reads as red/orange/yellow at-a-glance instead of muted tailwind tones.
  severityLow:      '#facc15', // saturated yellow (was amber #FBBF24)
  severityMod:      '#fb923c', // bright orange
  severityModerate: '#facc15', // saturated yellow
  severityHigh:     '#ef4444', // true red
  severityExtreme:  '#b91c1c', // deep crimson (was #dc2626)
  severityFallback: '#6b7280',
  // FHSZ-style "vintage" tints — kept lighter for layered legends.
  vintageVeryHigh:  '#ef4444', // bumped from light pink #f87171
  vintageHigh:      '#f97316', // bumped from #fb923c
  vintageModerate:  '#facc15', // bumped from pale #fde047
  vintageFallback:  '#9ca3af',
  // Green tokens for vegetation / containment / safe states.
  vegetationGreen:  '#22c55e', // green-500 — saturated for dark-mode landuse hint
  containmentGreen: '#16a34a', // green-600 — fire-contained / safe
  safeGreen:        '#84cc16', // lime-500 — alt vegetation
} as const;

// ── Helper: build a full palette from base tones ─────────────────

interface PaletteBase {
  mode: ThemeMode;
  landmass: string;
  water: string;
  landuse: string;
  minorRoad: string;
  majorRoad: string;
  cityLabel: string;
  minorLabel: string;
  contour: string;
  hillshadeShadow: string;
  hillshadeHighlight: string;
  building: string;
}

export function makePalette(base: PaletteBase): MapPalette {
  const isDark = base.mode === 'dark';
  return {
    ...base,
    minorRoadOp: isDark ? 0.6 : 0.4,
    majorRoadOp: isDark ? 0.7 : 0.5,
    textHalo: base.landmass,
    textHaloWidth: 1.5,
    textHaloBlur: 1,
    contourIndexOp: isDark ? 0.6 : 0.4,
    contourMinorOp: isDark ? 0.25 : 0.15,
    ...FIRE_TOKENS,
    // Light palettes need a visible beacon stroke ring
    beaconStroke: isDark ? '#151A28' : '#ffffff',
    beaconStrokeW: isDark ? 0 : 1,
  };
}

// ── Dark palettes ─────────────────────────────────────────────────

export const deepCanvas: MapPalette = makePalette({
  mode:       'dark',
  landmass:   '#151A28',
  water:      '#0D111A',
  landuse:    '#1E2538',
  minorRoad:  '#252D44',
  majorRoad:  '#3A4663',
  cityLabel:  '#9CA3AF',
  minorLabel: '#838B99',
  contour:    '#2A344D',
  hillshadeShadow:    '#000000',
  hillshadeHighlight: '#FFFFFF',
  building:   '#1E2538',
});

export const dusk: MapPalette = makePalette({
  mode:       'dark',
  landmass:   '#1A2035',
  water:      '#101624',
  landuse:    '#232B40',
  minorRoad:  '#2D3654',
  majorRoad:  '#45516E',
  cityLabel:  '#A8B0BE',
  minorLabel: '#8B939F',
  contour:    '#323D5A',
  hillshadeShadow:    '#000000',
  hillshadeHighlight: '#FFFFFF',
  building:   '#232B40',
});

export const twilight: MapPalette = makePalette({
  mode:       'dark',
  landmass:   '#1F2742',
  water:      '#151C30',
  landuse:    '#283248',
  minorRoad:  '#374160',
  majorRoad:  '#515E7A',
  cityLabel:  '#B4BCCA',
  minorLabel: '#8891A0',
  contour:    '#3A4667',
  hillshadeShadow:    '#000000',
  hillshadeHighlight: '#FFFFFF',
  building:   '#283248',
});

export const slate: MapPalette = makePalette({
  mode:       'dark',
  landmass:   '#263043',
  water:      '#1A2233',
  landuse:    '#2F3A50',
  minorRoad:  '#414D68',
  majorRoad:  '#5D6B86',
  cityLabel:  '#C0C7D3',
  minorLabel: '#969EAE',
  contour:    '#445070',
  hillshadeShadow:    '#000000',
  hillshadeHighlight: '#FFFFFF',
  building:   '#2F3A50',
});

export const brand: MapPalette = makePalette({
  mode:       'dark',
  landmass:   '#0f172a',
  water:      '#020617',
  landuse:    '#1e293b',
  minorRoad:  '#334155',
  majorRoad:  '#475569',
  cityLabel:  '#cbd5e1',
  minorLabel: '#94a3b8',
  contour:    '#334155',
  hillshadeShadow:    '#000000',
  hillshadeHighlight: '#FFFFFF',
  building:   '#1e293b',
});

// ── Light palettes ────────────────────────────────────────────────

export const daylight: MapPalette = makePalette({
  mode:       'light',
  landmass:   '#e2e8f0', // slate-200 — grayer base so darker overlays have contrast (was near-white #f8fafc)
  water:      '#93c5fd', // saturated blue-300 (was pale #bfdbfe)
  landuse:    '#bbf7d0', // brighter green for vegetation hint (was washed #dcfce7)
  minorRoad:  '#94a3b8', // slate-400 (was light #cbd5e1)
  majorRoad:  '#475569', // slate-600 — much darker for road visibility (was #94a3b8)
  cityLabel:  '#0f172a',
  minorLabel: '#334155',
  contour:    '#64748b',
  hillshadeShadow:    '#475569',
  hillshadeHighlight: '#ffffff',
  building:   '#475569', // slate-600 — readable against slate-200 base (was washed #94a3b8)
});

export const paper: MapPalette = makePalette({
  mode:       'light',
  landmass:   '#f1f5f9',
  water:      '#93c5fd',
  landuse:    '#d1fae5',
  minorRoad:  '#cbd5e1',
  majorRoad:  '#9ca3af',
  cityLabel:  '#1e293b',
  minorLabel: '#475569',
  contour:    '#9ca3af',
  hillshadeShadow:    '#64748b',
  hillshadeHighlight: '#ffffff',
  building:   '#9ca3af',
});

export const cloud: MapPalette = makePalette({
  mode:       'light',
  landmass:   '#e2e8f0',
  water:      '#7dd3fc',
  landuse:    '#bbf7d0',
  minorRoad:  '#94a3b8',
  majorRoad:  '#64748b',
  cityLabel:  '#0f172a',
  minorLabel: '#334155',
  contour:    '#64748b',
  hillshadeShadow:    '#475569',
  hillshadeHighlight: '#f8fafc',
  building:   '#64748b',
});

// ── Palette registry ──────────────────────────────────────────────

export const PALETTES: Record<PaletteName, MapPalette> = {
  deepCanvas,
  dusk,
  twilight,
  slate,
  brand,
  daylight,
  paper,
  cloud,
};

/** Get palette names filtered by theme mode. */
export function paletteNamesByMode(mode: ThemeMode): PaletteName[] {
  return (Object.keys(PALETTES) as PaletteName[]).filter(
    (name) => PALETTES[name].mode === mode,
  );
}

// ── Spacing Zone (SZ) tokens — inter-property building spacing ─────
//
// Shared by both demo.riskscan.ai and data-lake.forceshield.ai so the
// choropleth fill, the half-distance "appetite" circles, the on-select ring,
// the click popup, and the legend all read from one source of truth.
//
// SZ measures EDGE-TO-EDGE inter-property home separation — NOT defensible
// space. The SZ prefix and "Spacing Zone" naming are mandatory in any
// prospect-facing copy; never shorten an SZ value to `Z` or imply it is a
// defensible-space zone or compliance rating. (See the feature spec §4.)
//
// Risk ramp: red (touching, highest structure-to-structure exposure) →
// green (isolated, lowest). Unknown rows are a neutral gray and are excluded
// from the continuous ramp.

export const SPACING_ZONE_ORDER = ['SZ0', 'SZ1', 'SZ2', 'SZ3', 'SZ4'] as const;
export type SpacingZone = (typeof SPACING_ZONE_ORDER)[number];

/** Neutral gray for measured-but-unknown / not-a-zone rows. */
export const SPACING_UNKNOWN_COLOR = '#9ca3af'; // gray-400

export const SPACING_ZONE_COLORS: Record<SpacingZone, string> = {
  SZ0: '#ef4444', // imminent (0–5 ft) — highest
  SZ1: '#fb923c', // close (5–30 ft)
  SZ2: '#facc15', // moderate (30–100 ft)
  SZ3: '#84cc16', // separated (100–300 ft)
  SZ4: '#16a34a', // home-isolated (>300 ft, or no home in range) — lowest
};

/** Legend row copy — feet first (US underwriting default), SZ-prefixed. */
export const SPACING_ZONE_LABELS: Record<SpacingZone, string> = {
  SZ0: 'Imminent · 0–5 ft',
  SZ1: 'Close · 5–30 ft',
  SZ2: 'Moderate · 30–100 ft',
  SZ3: 'Separated · 100–300 ft',
  SZ4: 'Isolated · >300 ft or none in range',
};

/** Label for the Unknown swatch (not a Spacing Zone). */
export const SPACING_UNKNOWN_LABEL = 'Not measured';

/** Legend title + the mandatory "not defensible-space compliance" disclaimer. */
export const SPACING_ZONE_LEGEND_TITLE = 'Spacing Zone';
export const SPACING_ZONE_LEGEND_NOTE =
  'Edge-to-edge separation to the nearest neighboring home on another ' +
  'property. Not a defensible-space compliance rating.';

// ── FR-1(b): configurable / finer Spacing-Zone bands ──────────────────────────
//
// The shipped choropleth banded SZ0–SZ4 SERVER-SIDE (the baked `spacing_zone`
// string). FR-1(b) moves banding to the CLIENT so a deployment can pick finer
// thresholds (e.g. subdividing SZ1 5–30 ft into 5–10 / 10–20 / 20–30) with NO
// re-export — the export already emits the raw floats `nearest_combustible_m` /
// `nearest_combustible_all_m`, and the layer derives the band from those over a
// preset (see SpacingLayer.spacingBandColor). A preset is just a list of band
// UPPER EDGES + one color + one label per band.
//
// Honest scope (per the spec): the geometry/no-re-export half is genuinely low
// lift, but >5 bands is NOT "pure config" — it needs an N-entry color ramp + N
// labels + an N-row info-panel table. `makeSpacingBandPreset` generates those.

/** Feet → metres. US underwriting inputs are in feet; the raw spacing floats are
 *  metres, so presets are authored in feet and converted here. */
export const FT_TO_M = 0.3048;

/** A client-side Spacing-Zone banding preset.
 *
 *  `edgesM` are the band UPPER edges in METRES, strictly ascending. With
 *  `N = edgesM.length + 1` bands, band `i` is `SZ{i}` and covers
 *  `[edgesM[i-1], edgesM[i])` (open lower bound 0, open upper bound +∞). The
 *  FINAL band `SZ{N-1}` is the ISOLATED band: a finite distance `>` the last edge
 *  OR a null distance (no combustible neighbour in range) both land here — so the
 *  last edge MUST be the 300 ft isolation edge (`SZ4_M`); a preset topping out
 *  below 300 ft would wrongly fold "separated" (100–300 ft) into "isolated".
 *  `colors` and `labels` each have length `N` (one per band). Band ids are always
 *  `SZ0..SZ{N-1}` (the SZ naming discipline is mandatory — never `Z`). */
export interface SpacingBandPreset {
  id: string;
  edgesM: number[];
  colors: string[];
  labels: string[];
}

/** The 300 ft isolation edge, in metres — the mandatory top edge of any preset
 *  (mirrors the export's `SZ4_M`). */
export const SPACING_ISOLATION_EDGE_M = 300 * FT_TO_M; // 91.44

/** Canonical 5-band preset (SZ0–SZ4): identical thresholds / colors / labels to
 *  the legacy server-baked banding, so the default map is visually unchanged. */
export const SPACING_PRESET_DEFAULT: SpacingBandPreset = {
  id: 'default-5',
  edgesM: [5, 30, 100, 300].map((ft) => ft * FT_TO_M), // 1.524/9.144/30.48/91.44
  colors: SPACING_ZONE_ORDER.map((z) => SPACING_ZONE_COLORS[z]),
  labels: SPACING_ZONE_ORDER.map((z) => SPACING_ZONE_LABELS[z]),
};

/** Band id for index `i` — the mandatory `SZ{i}` coinage. */
export function spacingBandId(i: number): string {
  return `SZ${i}`;
}

/** The band INDEX (0-based) a raw edge-to-edge distance (metres) falls in under a
 *  preset, matching the server `_spacing_zone` boundary contract EXACTLY:
 *    - lower edges are exclusive-upper (`d < edge` → that band), so e.g.
 *      `SZ1_M <= d < SZ2_M` → SZ1;
 *    - the FINAL edge (the 300 ft isolation edge) is INCLUSIVE-upper
 *      (`d <= SZ4_M` → SZ3 "separated"), so exactly 300 ft is "separated", NOT
 *      "isolated" — this preserves the canonical exporter's `d <= SZ4_M → SZ3`;
 *    - `null`/`undefined` (no combustible neighbour in range) → the last band
 *      (isolated). `d` strictly greater than the last edge → isolated too.
 *  Used for the popup's displayed zone + the info-panel band table, so the
 *  client text matches the choropleth color under ANY (incl. finer) preset. */
export function spacingBandIndex(
  distM: number | null | undefined,
  preset: SpacingBandPreset,
): number {
  const e = preset.edgesM;
  if (distM === null || distM === undefined) return e.length; // isolated (last band)
  for (let i = 0; i < e.length; i++) {
    const inclusive = i === e.length - 1; // the 300 ft isolation edge is inclusive
    if (inclusive ? distM <= e[i] : distM < e[i]) return i;
  }
  return e.length; // > last edge → isolated
}

/** A one-line band-definition sentence for the info-panel/legend modal, generated
 *  from the ACTIVE preset so it never shows stale SZ0–SZ4 edges under a finer
 *  preset. e.g. "SZ0 0–5 ft · SZ1 5–10 ft · … · SZ7 >300 ft or none in range". */
export function spacingBandsTermText(preset: SpacingBandPreset): string {
  return preset.labels
    .map((label, i) => `${spacingBandId(i)} ${label.replace(/^[^·]*·\s*/, '')}`)
    .join(' · ');
}

function _lerpHex(a: string, b: string, t: number): string {
  const h = (s: string) => [1, 3, 5].map((i) => parseInt(s.slice(i, i + 2), 16));
  const [ar, ag, ab] = h(a);
  const [br, bg, bb] = h(b);
  const c = (x: number, y: number) =>
    Math.round(x + (y - x) * t).toString(16).padStart(2, '0');
  return `#${c(ar, br)}${c(ag, bg)}${c(ab, bb)}`;
}

/** Sample the canonical red→green risk ramp (through the SZ0–SZ4 anchors) at
 *  `t ∈ [0, 1]`, so a generated N-band palette reads like the 5-band default. */
export function spacingRampColor(t: number): string {
  const anchors = SPACING_ZONE_ORDER.map((z) => SPACING_ZONE_COLORS[z]);
  const clamped = Math.max(0, Math.min(1, t));
  const seg = clamped * (anchors.length - 1);
  const i = Math.min(Math.floor(seg), anchors.length - 2);
  return _lerpHex(anchors[i], anchors[i + 1], seg - i);
}

/** Build an N-band preset from band upper EDGES in FEET (ascending; the last MUST
 *  be 300 to preserve the isolation edge). Generates the N-color red→green ramp
 *  and N feet-first labels. For the canonical [5,30,100,300] it returns the same
 *  thresholds as the default (colors/labels are the generic generated form — use
 *  `SPACING_PRESET_DEFAULT` when you want the descriptive Imminent/Close/… copy).
 */
export function makeSpacingBandPreset(id: string, edgesFt: number[]): SpacingBandPreset {
  if (edgesFt.length === 0) throw new Error('preset needs ≥1 edge');
  // Edges MUST be WHOLE FEET. The size-constrained tile export quantizes the raw
  // spacing distances edge-aware against the integer-foot grid (round_spacing_
  // geojson.py:_round_band) — that is what lets it shrink the value pool without
  // ever crossing a band edge. A fractional-foot edge (e.g. 6.57 ft = 2.003 m)
  // could fall mid-rounding-interval and mis-band, so it is rejected here rather
  // than silently corrupting the choropleth.
  for (const ft of edgesFt) {
    if (!Number.isInteger(ft) || ft <= 0) {
      throw new Error(
        `Spacing preset edges must be positive whole feet (the tile quantizer ` +
        `protects the integer-foot grid), got ${JSON.stringify(edgesFt)}`);
    }
  }
  // Edges MUST be strictly ascending — MapLibre `step` requires ascending stops,
  // and a non-monotonic preset would silently mis-band (or throw at paint time).
  for (let i = 1; i < edgesFt.length; i++) {
    if (edgesFt[i] <= edgesFt[i - 1]) {
      throw new Error(
        `Spacing preset edges must be strictly ascending, got ${JSON.stringify(edgesFt)}`);
    }
  }
  const last = edgesFt[edgesFt.length - 1];
  if (last !== 300) {
    throw new Error(
      `Spacing preset top edge must be 300 ft (the isolation edge), got ${last}; ` +
      'a lower top edge would fold "separated" (100–300 ft) into "isolated".');
  }
  const n = edgesFt.length + 1; // bands
  const edgesM = edgesFt.map((ft) => ft * FT_TO_M);
  const colors = Array.from({ length: n }, (_, i) =>
    spacingRampColor(n === 1 ? 0 : i / (n - 1)));
  const labels = Array.from({ length: n }, (_, i) => {
    if (i === 0) return `Imminent · 0–${edgesFt[0]} ft`;
    if (i === n - 1) return `Isolated · >${edgesFt[n - 2]} ft or none in range`;
    return `${edgesFt[i - 1]}–${edgesFt[i]} ft`;
  });
  return { id, edgesM, colors, labels };
}

/** Finer example preset (8 bands) — subdivides SZ1 (5–30 ft) into 5–10 / 10–20 /
 *  20–30 per Michael Salem's ask, and SZ2 into 30–50 / 50–100, keeping the 300 ft
 *  isolation edge. Selectable per deployment for dense urban books. */
export const SPACING_PRESET_FINER: SpacingBandPreset =
  makeSpacingBandPreset('finer-8', [5, 10, 20, 30, 50, 100, 300]);
